/**
 * ReMa Environment + Tenant ID bar.
 *
 * Shown at the top of ReMa create forms. Displays the tenant ID for each tenant
 * (Conex / Farmgrid) across every environment, and lets the user pick the
 * environment via radio buttons. The selected environment determines the tenant
 * ID used when building the resource URI (see getTenantId).
 */

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { TENANT_IDS, REMA_ENVIRONMENTS, type RemaEnvironment } from '@/hooks/useRemaUriBuilder'

const ENV_LABELS: Record<RemaEnvironment, string> = {
  development: 'Development',
  staging: 'Staging',
  production: 'Production',
}

interface RemaEnvironmentBarProps {
  /** Currently selected environment */
  environment: RemaEnvironment
  /** Called when the user picks a different environment */
  onEnvironmentChange: (env: RemaEnvironment) => void
  /** Optional current namespace (cnx/frg) — highlights the matching tenant column */
  namespace?: string
}

export function RemaEnvironmentBar({ environment, onEnvironmentChange, namespace }: RemaEnvironmentBarProps) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-slate-800">Tenant &amp; Environment</p>
        <p className="text-xs text-muted-foreground">
          Select the environment — it sets the tenant ID used in the resource URI.
        </p>
      </div>

      <RadioGroup
        value={environment}
        onValueChange={(v) => onEnvironmentChange(v as RemaEnvironment)}
        className="mt-3 flex flex-wrap gap-4"
      >
        {REMA_ENVIRONMENTS.map((env) => (
          <Label
            key={env}
            htmlFor={`rema-env-${env}`}
            className="flex cursor-pointer items-center gap-2 text-sm font-normal"
          >
            <RadioGroupItem id={`rema-env-${env}`} value={env} />
            {ENV_LABELS[env]}
          </Label>
        ))}
      </RadioGroup>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-4 font-medium">Environment</th>
              <th className={cn('py-1 pr-4 font-medium', namespace === 'cnx' && 'text-slate-900')}>Conex (cnx)</th>
              <th className={cn('py-1 font-medium', namespace === 'frg' && 'text-slate-900')}>Farmgrid (frg)</th>
            </tr>
          </thead>
          <tbody>
            {REMA_ENVIRONMENTS.map((env) => {
              const isActive = env === environment
              return (
                <tr key={env} className={cn(isActive && 'rounded bg-primary/10 font-medium text-slate-900')}>
                  <td className="py-1 pr-4">{ENV_LABELS[env]}</td>
                  <td className={cn('py-1 pr-4 font-mono', namespace === 'cnx' && isActive && 'text-primary')}>
                    {TENANT_IDS.cnx[env]}
                  </td>
                  <td className={cn('py-1 font-mono', namespace === 'frg' && isActive && 'text-primary')}>
                    {TENANT_IDS.frg[env]}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
